import { useRef, useState, useEffect, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import _ from 'lodash';
import { useLocation } from 'react-router-dom';

import useModal from '../../hooks/useModal';
import useWhiteLabelComponent from '../../hooks/useWhiteLabelComponent';
import usePopup from '../../hooks/usePopup';
import useTooltip from '../../hooks/useTooltip';

import { isReadableContrast, VL_BLACK, WHITE } from '../../utils/colours';
import { isCCTVConnect, isLegacyMobileApp, isMobileApp } from '../../utils/getHost';
import translationKeys from '../../translations/keys';

// Mobile menu icons
import { ReactComponent as HamburgerIcon } from '../../assets/images/icons/burger-menu.svg';
import { ReactComponent as CloseIcon } from '../../assets/images/icons/close.svg';
import { ReactComponent as AnalyticsIcon } from '../../assets/images/icons/analytics.svg';

import Logo from '../logo';
import Span from '../span';
import Popup from '../popup';
import Tooltip from '../tooltip';
import BottomTabBar from './BottomTabBar';
import {
    SidebarAbsoluteContainer,
    NavLinks,
    MainNavLinks,
    NavLink,
    MainNavLink,
    NavItem,
    MainNavItem,
    Divider,
    Footer,
    Copyright,
    FooterLink,
    FooterButton,
    Header,
    Menu,
    MobileMenuToggle,
    LogoContainer,
    NavButton,
    SidebarRelativeContainer,
    IconContainer,
    CollapsedLogoContainer,
    BetaTag,
} from './Sidebar.styles';
import { Flex } from '../layout';

const SIDEBAR_GROUP_CONFIG = {
    analytics: {
        title: translationKeys.technical.ANALYTICS_ADDONS,
        icon: AnalyticsIcon
    }
};

// Delay in ms before showing tooltip (shorter than browser default ~500-1000ms)
const TOOLTIP_DELAY = 300;

const VISUALLY_HIDDEN_STYLES = {
    position: 'absolute',
    left: '-10000px',
    top: 'auto',
    width: '1px',
    height: '1px',
    overflow: 'hidden'
};

// Component that wraps a nav item with a tooltip
const TooltipNavItem = ({ children, tooltipDescription, tooltipMaxWidth, tooltipNoWrap, isMobileScreenWidth }) => {
    const [tooltipProps, triggerProps] = useTooltip({
        placement: 'right',
        delay: TOOLTIP_DELAY,
        showOnMobile: false,
        fixedPositioning: true
    });
    const tooltipIdRef = useRef(_.uniqueId('sidebar-tooltip-'));
    const descriptionId = tooltipDescription ? `${tooltipIdRef.current}-description` : undefined;
    const shouldShowTooltip = !isMobileScreenWidth && Boolean(tooltipDescription);

    return (
        <>
            {children({ triggerProps: shouldShowTooltip ? triggerProps : undefined, descriptionId })}
            {tooltipDescription && (
                <span id={descriptionId} style={VISUALLY_HIDDEN_STYLES}>
                    {tooltipDescription}
                </span>
            )}
            {shouldShowTooltip && (
                <Tooltip
                    {...tooltipProps}
                    id={tooltipIdRef.current}
                    style={{
                        ...tooltipProps.style,
                        width: 'max-content',
                        maxWidth: tooltipMaxWidth ?? '250px',
                        whiteSpace: tooltipNoWrap ? 'nowrap' : undefined
                    }}
                >
                    {tooltipDescription}
                </Tooltip>
            )}
        </>
    );
};

// Component that renders a MainNavItem with a popup containing items from the same group
const PopupNavItem = ({ group, groupItems, isMobileScreenWidth, textColour, alternativeTextColour, accentColour, accentColour2, isVideoloft, backgroundColour, mobileBreakpoint, t }) => {
    // Use the first item's icon for the main nav item, or a default
    const firstIcon = groupItems[0]?.icon;
    const MainIcon = SIDEBAR_GROUP_CONFIG[group]?.icon || firstIcon;

    // Set up popup with right placement
    const [popupProps, triggerProps] = usePopup({
        placement: 'right',
        arrow: false,
        fixedPositioning: true,
        offset: [0, 0]
    });

    return (
        <>
            <MainNavItem
                key={group}
                $mobileBreakpoint={mobileBreakpoint}
                $desktopOnly={true}
                {...triggerProps}
            >
                <MainNavLink
                    $textColour={textColour}
                    $alternativeTextColour={alternativeTextColour}
                    $accentColour={accentColour}
                    $accentColour2={isVideoloft ? accentColour2 : undefined}
                    $backgroundColour={backgroundColour}
                    to={`/${group}group`}
                    $mobileBreakpoint={mobileBreakpoint}
                    onClick={(e) => e.preventDefault()} // Prevent navigation
                >
                    <IconContainer>
                        {MainIcon && <MainIcon alt={group} />}
                    </IconContainer>
                    <Span>{t(SIDEBAR_GROUP_CONFIG[group]?.title)}</Span>
                </MainNavLink>
            </MainNavItem>
            {groupItems.map(({ text, path, icon, beta }) => {
                const Icon = icon;
                return (
                    <MainNavItem
                        key={group+path}
                        title={
                            !isMobileScreenWidth ? group : undefined
                        }
                        $mobileBreakpoint={mobileBreakpoint}
                        $mobileOnly={true}
                    >
                        <MainNavLink
                            $textColour={textColour}
                            $alternativeTextColour={alternativeTextColour}
                            $accentColour={accentColour}
                            $accentColour2={isVideoloft ? accentColour2 : undefined}
                            $backgroundColour={backgroundColour}
                            to={path}
                            $mobileBreakpoint={mobileBreakpoint}
                            style={{ height: '100%' }}
                        >
                            <IconContainer>
                                <Icon alt={text} />
                            </IconContainer>
                            <Span>{text}</Span>
                            {beta && (
                                <BetaTag style={{ fontSize: '10px' }}>{t(translationKeys.technical.BETA)}</BetaTag>
                            )}
                        </MainNavLink>
                    </MainNavItem>
                );
            })}
            
            {/* Popup with group items */}
            <Popup
                {...popupProps}
                style={{
                    ...popupProps.style,
                    backgroundColor: backgroundColour,
                    color: textColour,
                    border: 'none',
                    borderRadius: 1,
                    padding: 0
                }}
                width={Math.min(400, groupItems.length * 80)} // Dynamic width based on number of items
                maxHeight={80}
            >
                <Flex row justifyContent="center">
                    {groupItems.map(({ text, path, icon, beta }) => {
                        const Icon = icon;
                        return (
                            <div key={path} style={{ flex: 1, textAlign: 'center' }}>
                                <MainNavLink
                                    $textColour={textColour}
                                    $alternativeTextColour={alternativeTextColour}
                                    $accentColour={accentColour}
                                    $accentColour2={isVideoloft ? accentColour2 : undefined}
                                    $backgroundColour={backgroundColour}
                                    to={path}
                                    $mobileBreakpoint={mobileBreakpoint}
                                    style={{ height: '100%' }}
                                >
                                    <IconContainer>
                                        <Icon alt={text} />
                                    </IconContainer>
                                    <Span style={{ fontSize: '12px' }}>{text}</Span>
                                    {beta && (
                                        <BetaTag style={{ fontSize: '10px' }}>{t(translationKeys.technical.BETA)}</BetaTag>
                                    )}
                                </MainNavLink>
                            </div>
                        );
                    })}
                </Flex>
            </Popup>
        </>
    );
};

/** Sidebar used by AppLayout. */
const Sidebar = ({
    mainLinks,
    secondaryLinks,
    footerLinks = [],
    mobileBreakpoint = 800,
    t,
    tReady,
    ...props
}) => {
    // Mobile menu is very similar to a modal
    // useModal will handle trapping focus and preventing scrolling
    const [{ show: mobileMenuOpen, containerRef: mobileMenuContainerRef, closeRef: closeMobileMenuRef }, showMobileMenu, closeMobileMenu] = useModal();

    const { logo, appLayoutLogo: logoSrc, appLayoutLogoSmall: collapsedLogoSrc, returnLink: logoLink, whiteOverAccent, navColour: backgroundColour,  accentColour: wlAccentColour, accentColour2, copyrightName, isVideoloft } = useWhiteLabelComponent();
    const accentColour = wlAccentColour === backgroundColour ? WHITE(75) : wlAccentColour;

    const location = useLocation();

    const determineTextColourOverAccent = () => {
        if (isCCTVConnect) {
            if (isReadableContrast(accentColour, WHITE())) {
                return WHITE();
            } else return VL_BLACK();
        } else {
            return VL_BLACK();
        }
    };    
    
    const determineNavTextColour = () => {
        if (_.isNil(accentColour2) && whiteOverAccent) {
            return WHITE();
        }
        if (isReadableContrast(backgroundColour, VL_BLACK())) {
            return VL_BLACK();
        } else return WHITE();
    }

    const textColour = determineNavTextColour();

    const alternativeTextColour = determineTextColourOverAccent();

    // On mobile, menu fills screen when open
    const [isMobileScreenWidth, setIsMobileScreenWidth] = useState(isMobileApp || window.innerWidth <= mobileBreakpoint);
    useEffect(() => {
        if (!isMobileApp) {
            const update = () => setIsMobileScreenWidth(window.innerWidth <= mobileBreakpoint);

            update();

            window.addEventListener('resize', update);

            return () => window.removeEventListener('resize', update);
        }
    }, [mobileBreakpoint]);

    // Always show mobile view on mobile app
    // Otherwise we get a mix of both logic and it doesn't work
    if (isMobileApp) {
        mobileBreakpoint = Number.MAX_SAFE_INTEGER;
    }
    
    // Need useModal to know when mobile menu is closed due to screen width to make sure focus doesn't stay trapped
    useEffect(() => {
        if (!isMobileScreenWidth) {
            closeMobileMenu();
        }
    }, [isMobileScreenWidth, closeMobileMenu]);

    // Close menu when location changes, either by user clicking link or through browser back/forward buttons
    useLayoutEffect(() => {
        closeMobileMenu();
    }, [location, closeMobileMenu]);

    return (
        <SidebarRelativeContainer
            $mobileBreakpoint={mobileBreakpoint}
        >
            <SidebarAbsoluteContainer
                ref={mobileMenuContainerRef}
                $backgroundColour={backgroundColour}
                $mobileBreakpoint={mobileBreakpoint}
                $mobileMenuOpen={mobileMenuOpen}
                {...props}
            >
                {/* Header shown on desktop and mobile */}
                {
                    !isMobileApp && (
                        <Header
                            $mobileBreakpoint={mobileBreakpoint}
                            $noCollapsedLogo={!collapsedLogoSrc || !logo}
                        >
                            {/* Logo on mobile */}
                            <LogoContainer
                                $mobileBreakpoint={mobileBreakpoint}
                                $noCollapsedLogo={!collapsedLogoSrc || !logo}
                            >
                                <Logo
                                    src={logoSrc ?? logo}
                                    link={logoLink}
                                    // Should not be able to focus element when hidden
                                    tabIndex={!isMobileScreenWidth && collapsedLogoSrc ? -1 : undefined}
                                />
                            </LogoContainer>

                            {/* Desktop logo */}
                            {(collapsedLogoSrc || logo) && (
                                <CollapsedLogoContainer
                                    $mobileBreakpoint={mobileBreakpoint}
                                >
                                    <Logo
                                        src={collapsedLogoSrc ?? logo}
                                        link={logoLink}
                                        // Should not be able to focus element when hidden
                                        tabIndex={isMobileScreenWidth ? -1 : undefined}
                                        alt="App logo"
                                    />
                                </CollapsedLogoContainer>
                            )}

                            {/* Hamburger only shown on mobile */}
                            {!isMobileApp && (
                                <MobileMenuToggle
                                    $mobileBreakpoint={mobileBreakpoint}
                                    ref={closeMobileMenuRef}
                                    onClick={mobileMenuOpen ? closeMobileMenu : showMobileMenu}
                                    aria-expanded={mobileMenuOpen}
                                    aria-controls="navigation-menu"
                                    aria-haspopup="true"
                                    data-testid="mobile-menu-toggle"
                                    $colour={textColour}
                                >
                                    {mobileMenuOpen ? <CloseIcon /> : <HamburgerIcon />}
                                </MobileMenuToggle>
                            )}
                        </Header>
                    )
                }

                {/* Everything that appears hidden on mobile wrapped in Menu component. Id is referred to by MobileMenuToggle. */}
                <Menu
                    id="navigation-menu"
                    $mobileBreakpoint={mobileBreakpoint}
                    $mobileMenuOpen={mobileMenuOpen}
                >

                    {/* 

                    We used to have a camera group selector at the top of the sidebar. I'm leaving the code for it below
                    as I'm envisaging that one day we may have some sort of profile/account selector (e.g. for installers to
                    choose between installer and demo account, or for users to choose which organisation's cameras they are
                    viewing) that will need a similar UI.
                    
                    {cameraGroups?.length > 0 && selectedCameraGroup && (
                        <CameraGroupSelector>
                            <CameraGroupSelectorButton
                                $colour={textColour}
                                $collapsed={collapsedDesktopMenu}
                                $mobileBreakpoint={mobileBreakpoint}
                                {...popupTriggerButtonProps}
                                title={
                                    collapsedDesktopMenu &&
                                    !isMobileScreenWidth
                                        ? cameraGroupText
                                        : undefined
                                }
                            >
                                <IconContainer>
                                    <CameraGroupsIcon alt={cameraGroupText} />
                                </IconContainer>
                                <SelectedCameraGroupText
                                    $collapsed={collapsedDesktopMenu}
                                    $mobileBreakpoint={mobileBreakpoint}
                                >
                                    <Span fontSize="75%" bold>{cameraGroupText}</Span>
                                    <Span>
                                        {selectedCameraGroup.name}
                                    </Span>
                                </SelectedCameraGroupText>
                            </CameraGroupSelectorButton>
                            <PopupList
                                type="select"
                                maxListHeight={600}
                                options={cameraGroups
                                    .map(({ name, id }) => ({
                                        label: name,
                                        key: id,
                                        selected: selectedCameraGroup.id === id,
                                        action: editCameraGroup && id > 0 ? {
                                            text: editCameraGroupText,
                                            onClick: () => {
                                                // Close popup when "Edit" clicked
                                                popupProps?.closePopup();
                                                editCameraGroup(id);
                                                closeMobileMenu();
                                            }
                                        } : undefined
                                    }))}
                                onChange={(id) => {
                                    changeCameraGroup(id);
                                    closeMobileMenu();
                                }}
                                accentColour={accentColour}
                                {...popupProps}
                            />
                        </CameraGroupSelector>
                    )} */}

                    {/* Links for top section of sidebar (shown in bottom bar instead in mobile app). */}
                    {
                        !isMobileApp && (
                            <>
                                <MainNavLinks>
                                    {(() => {
                                        // Group mainLinks by their group property
                                        const groupedLinks = {};
                                        const ungroupedLinks = [];
                                        
                                        mainLinks.forEach(link => {
                                            if (link.group) {
                                                if (!groupedLinks[link.group]) {
                                                    groupedLinks[link.group] = [];
                                                }
                                                groupedLinks[link.group].push(link);
                                            } else {
                                                ungroupedLinks.push(link);
                                            }
                                        });
                                        
                                        const renderedItems = [];
                                        
                                        ungroupedLinks.forEach(({ text, path, icon, beta, tooltipDescription, tooltipMaxWidth, tooltipNoWrap }) => {
                                            const Icon = icon;
                                            renderedItems.push(
                                                <TooltipNavItem
                                                    key={path}
                                                    tooltipDescription={tooltipDescription}
                                                    tooltipMaxWidth={tooltipMaxWidth}
                                                    tooltipNoWrap={tooltipNoWrap}
                                                    isMobileScreenWidth={isMobileScreenWidth}
                                                >
                                                    {({ triggerProps, descriptionId } = {}) => (
                                                        <MainNavItem
                                                            ref={triggerProps?.ref}
                                                            onMouseEnter={triggerProps?.onMouseEnter}
                                                            onMouseLeave={triggerProps?.onMouseLeave}
                                                            onFocus={triggerProps?.onFocus}
                                                            onBlur={triggerProps?.onBlur}
                                                        >
                                                            <MainNavLink
                                                                $textColour={textColour}
                                                                $alternativeTextColour={alternativeTextColour}
                                                                $accentColour={accentColour}
                                                                $accentColour2={isVideoloft ? accentColour2 : undefined}
                                                                $backgroundColour={backgroundColour}
                                                                to={path}
                                                                $mobileBreakpoint={mobileBreakpoint}
                                                                aria-describedby={descriptionId}
                                                            >
                                                                <IconContainer>
                                                                    <Icon alt={text} />
                                                                </IconContainer>
                                                                <Span>{text}</Span>
                                                                {beta && (
                                                                    <BetaTag>{t(translationKeys.technical.BETA)}</BetaTag>
                                                                )}
                                                            </MainNavLink>
                                                        </MainNavItem>
                                                    )}
                                                </TooltipNavItem>
                                            );
                                        });

                                        Object.entries(groupedLinks).forEach(([group, groupItems]) => {
                                            renderedItems.push(
                                                <PopupNavItem
                                                    key={group}
                                                    group={group}
                                                    groupItems={groupItems}
                                                    isMobileScreenWidth={isMobileScreenWidth}
                                                    textColour={textColour}
                                                    alternativeTextColour={alternativeTextColour}
                                                    accentColour={accentColour}
                                                    accentColour2={accentColour2}
                                                    isVideoloft={isVideoloft}
                                                    backgroundColour={backgroundColour}
                                                    mobileBreakpoint={mobileBreakpoint}
                                                    t={t}
                                                />
                                            );
                                        });
                                        
                                        return renderedItems;
                                    })()}
                                </MainNavLinks>
                                
                                {/* Dividing line */}
                                <Divider $colour={accentColour} />
                            </>
                        )
                    }

                    {/* Links for lower section of sidebar. */}
                    <NavLinks>
                        {secondaryLinks.map(({ text, path, action, icon, tooltipDescription, tooltipMaxWidth, tooltipNoWrap }) => {
                            const Icon = icon;

                            return (
                                <TooltipNavItem
                                    key={path || action}
                                    tooltipDescription={tooltipDescription}
                                    tooltipMaxWidth={tooltipMaxWidth}
                                    tooltipNoWrap={tooltipNoWrap}
                                    isMobileScreenWidth={isMobileScreenWidth}
                                >
                                    {({ triggerProps, descriptionId } = {}) => (
                                        <NavItem
                                            ref={triggerProps?.ref}
                                            onMouseEnter={triggerProps?.onMouseEnter}
                                            onMouseLeave={triggerProps?.onMouseLeave}
                                            onFocus={triggerProps?.onFocus}
                                            onBlur={triggerProps?.onBlur}
                                            $mobileBreakpoint={mobileBreakpoint}
                                        >
                                            {path ? (
                                                <NavLink
                                                    to={path}
                                                    $textColour={textColour}
                                                    $alternativeTextColour={alternativeTextColour}
                                                    $backgroundColour={backgroundColour}
                                                    $accentColour={accentColour}
                                                    $accentColour2={isVideoloft ? accentColour2 : undefined}
                                                    $mobileBreakpoint={mobileBreakpoint}
                                                    aria-describedby={descriptionId}
                                                >
                                                    <IconContainer>
                                                        <Icon />
                                                    </IconContainer>
                                                    <Span>{text}</Span>
                                                </NavLink>
                                            ) : (
                                                <NavButton
                                                    onClick={() => {
                                                        action();
                                                    }}
                                                    $textColour={textColour}
                                                    $alternativeTextColour={alternativeTextColour}
                                                    $backgroundColour={backgroundColour}
                                                    $accentColour={accentColour}
                                                    $mobileBreakpoint={mobileBreakpoint}
                                                    aria-describedby={descriptionId}
                                                >
                                                    <IconContainer>
                                                        <Icon />
                                                    </IconContainer>
                                                    <Span>{text}</Span>
                                                </NavButton>
                                            )}
                                        </NavItem>
                                    )}
                                </TooltipNavItem>
                            );
                        })}
                    </NavLinks>

                    {/* Copyright, terms, privacy */}
                    {
                        footerLinks.length > 0 && (
                            <Footer
                                $colour={textColour}
                                $mobileBreakpoint={mobileBreakpoint}
                            >
                                {footerLinks.map(({ text, path, action }) => (
                                    path ? (
                                        <FooterLink
                                            key={path}
                                            to={path}
                                        >
                                            {text}
                                        </FooterLink>
                                    ) : (
                                        <FooterButton
                                            key={text}
                                            onClick={() => {
                                                action();
                                            }}
                                            $colour={textColour}
                                        >
                                            {text}
                                        </FooterButton>
                                    )
                                ))}
                                {copyrightName && (
                                    <Copyright>&copy; 2025 {copyrightName}</Copyright>
                                )}
                            </Footer>
                        )
                    }
                    
                </Menu>

                {
                    isMobileApp && !isLegacyMobileApp && (
                        <BottomTabBar
                            tabs={[
                                // Flatten grouped and ungrouped links for mobile bottom bar
                                ...mainLinks
                                    .filter(link => !link.group) // Show ungrouped links as is
                                    .map(link => ({
                                        ...link,
                                        label: link.text
                                    })),
                                // For grouped links, show individual items in bottom bar
                                ...mainLinks
                                    .filter(link => link.group)
                                    .map(link => ({
                                        ...link,
                                        label: link.text
                                    })),
                                {
                                    label: t(translationKeys.navigation.sidebar.MORE),
                                    icon: HamburgerIcon
                                }
                            ]}
                            currentPath={location.pathname}
                            menuOpen={mobileMenuOpen}
                            onMoreClick={showMobileMenu}
                            accentColour={accentColour}
                            textColour={textColour}
                            backgroundColour={backgroundColour}
                            alternativeTextColour={alternativeTextColour}
                        />
                    )
                }
            </SidebarAbsoluteContainer>
        </SidebarRelativeContainer>
    );
};

Sidebar.propTypes = {
    /** Logo URL. */
    logoSrc: PropTypes.string,
    /** URL of smaller logo to use when sidebar collapsed. */
    collapsedLogoSrc: PropTypes.string,
    /** Href value for anchor tag that wraps logo. If no value given for this prop, anchor tag is not used. */
    logoLink: PropTypes.string,
    /** Background colour. */
    backgroundColour: PropTypes.string,
    /** Links to go in upper half of sidebar. */
    mainLinks: PropTypes.arrayOf(
        PropTypes.shape({
            /** Text for nav link. */
            text: PropTypes.string.isRequired,
            /** Link destination. */
            path: PropTypes.string.isRequired,
            /** Icon component to render. */
            icon: PropTypes.elementType.isRequired,
            /** Show "BETA" next to link. */
            beta: PropTypes.bool,
            /** Group name for grouping links together in a popup. */
            group: PropTypes.string,
            /** Description shown in tooltip and read by screen readers. */
            tooltipDescription: PropTypes.string,
            /** Max width for tooltip content (defaults to 250px). */
            tooltipMaxWidth: PropTypes.string,
            /** Prevent tooltip content from wrapping. */
            tooltipNoWrap: PropTypes.bool
        })
    ).isRequired,
    /** Links to go in lower half of navbar. */
    secondaryLinks: PropTypes.arrayOf(PropTypes.shape({
        /** Text for nav link. */
        text: PropTypes.string.isRequired,
        /** Link destination. One of `path` or `action` must be present. */
        path: PropTypes.string,
        /** Called on click. Nav item will be a button rather than link. One of `path` or `action` must be present. */
        action: PropTypes.func,
        /** Icon component to render. */
        icon: PropTypes.elementType.isRequired,
        /** Description shown in tooltip and read by screen readers. */
        tooltipDescription: PropTypes.string,
        /** Max width for tooltip content (defaults to 250px). */
        tooltipMaxWidth: PropTypes.string,
        /** Prevent tooltip content from wrapping. */
        tooltipNoWrap: PropTypes.bool
    })).isRequired,
    /** Colour to use for highlighted elements, like selected nav link. */
    accentColour: PropTypes.string,
    /** Colour to use for non-active nav links. */
    fadedTextColour: PropTypes.string,
    /** Colour used for non-faded text. */
    textColour: PropTypes.string,
    /** Links to show at bottom of sidebar (e.g. privacy policy). */
    footerLinks: PropTypes.arrayOf(
        PropTypes.shape({
            /** Text for link. */
            text: PropTypes.string.isRequired,
            /** Link destination. */
            path: PropTypes.string.isRequired,
        })
    ),
    /** Mobile view shown for screen widths up to and including this value (in px). */
    mobileBreakpoint: PropTypes.number,
    /** Name to use in copyright. If this is null, no copyright will be shown. */
    copyrightName: PropTypes.string
};

export default withTranslation()(Sidebar);
